<template>
  <div class="channel-search-page">
    <!-- 头部 -->
    <div class="channel-search-header">
      <van-icon name="arrow-left" size="20" color="#333" style="cursor: pointer;" @click="$router.back()" />
      <div class="channel-search-search-inner" style="flex: 1; margin: 0 8px;">
        <van-icon name="search" size="14" color="#999" />
        <input v-model="searchQuery" type="text" :placeholder="$t('channelSearch_placeholder')"
          style="flex: 1; border: none; background: transparent; padding: 8px; font-size: 14px; outline: none;"
          @keyup.enter="search" />
        <van-icon v-if="searchQuery" name="clear" size="14" color="#999" style="cursor: pointer; flex-shrink: 0;"
          @click="clearSearch" />
      </div>
    </div>

    <!-- 搜索 tab -->
    <div class="search-popup-tabs">
      <div class="search-popup-tab" :class="{ active: activeTab === 'all' }" @click="switchTab('all')">
        {{ $t('channelSearch_tabAllMessages') }}
      </div>
      <div class="search-popup-tab" :class="{ active: activeTab === 'mine' }" @click="switchTab('mine')">
        {{ $t('channelSearch_tabMyMessages') }}
      </div>
    </div>

    <!-- 结果列表 -->
    <div class="search-results">
      <van-pull-refresh v-model="refreshing" :loading-text="$t('loading')" @refresh="onRefresh">
        <div v-if="loading && currentSessions.length === 0" style="display: flex; justify-content: center; padding: 40px;">
          <van-loading size="24" />
        </div>

        <div v-else-if="currentSessions.length === 0 && !loading && hasLoaded" style="text-align: center; padding: 40px; color: #8A8A8A;">
          <van-icon name="search" size="48" color="#ddd" />
          <div style="margin-top: 16px;">{{ $t('noData') }}</div>
        </div>

        <div v-else-if="currentSessions.length === 0 && !loading && !hasLoaded && activeTab === 'all' && !searchQuery.trim()" style="text-align: center; padding: 40px; color: #8A8A8A;">
          <van-icon name="search" size="48" color="#ddd" />
          <div style="margin-top: 16px;">{{ $t('channelSearch_placeholder') }}</div>
        </div>

        <div v-for="session in currentSessions" :key="session.chatSessionId" class="search-result-item"
          @click="openSessionDetail(session)">
          <!-- User Msg -->
          <div v-if="session.userMsg" class="result-usermsg">{{ $common.formatUserMsgPreview(session.userMsg) }}</div>
          <!-- AI Summary -->
          <div v-if="session.aiSummary" :class="['result-ai-summary', session.userMsg ? 'has-user-msg' : '']">
            <span class="result-ai-icon">✨</span>
            <span class="result-ai-text">{{ session.aiSummary }}</span>
          </div>
          <div class="result-meta">
            <img v-if="session.fromUserAvatar" :src="$common.getFullImageUrl(session.fromUserAvatar, $config, 's')" class="result-avatar" />
            <div v-else class="result-avatar-placeholder">{{ (session.fromUserName || '?')[0] }}</div>
            <span>{{ session.fromUserName || $t('anonymous') }}</span>
            <span v-if="session.createdAt"> · {{ $common.formatSmartTime(session.createdAt, $i18n.locale) }}</span>
          </div>
        </div>

        <div v-if="loading && currentSessions.length > 0" style="display: flex; justify-content: center; padding: 16px;">
          <van-loading size="20" />
        </div>
      </van-pull-refresh>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'
import { Icon, Toast, PullRefresh, Empty, Loading } from 'vant'

Vue.use(Icon)
Vue.use(Toast)
Vue.use(PullRefresh)
Vue.use(Empty)
Vue.use(Loading)

export default {
  name: 'ChannelSearchSessions',
  data() {
    return {
      channelId: '',
      searchQuery: '',
      activeTab: 'all',
      // All messages tab
      allSessions: [],
      allLoading: false,
      allHasLoaded: false,
      allCurPage: 1,
      allHasMore: true,
      // My messages tab
      mineSessions: [],
      mineLoading: false,
      mineHasLoaded: false,
      mineCurPage: 1,
      mineHasMore: true,
      // Common
      refreshing: false,
      pageSize: 20
    }
  },
  computed: {
    loading() {
      return this.activeTab === 'all' ? this.allLoading : this.mineLoading
    },
    hasLoaded() {
      return this.activeTab === 'all' ? this.allHasLoaded : this.mineHasLoaded
    },
    hasMore() {
      return this.activeTab === 'all' ? this.allHasMore : this.mineHasMore
    },
    currentSessions() {
      return this.activeTab === 'all' ? this.allSessions : this.mineSessions
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.channelId = String(newVal)
          // onlyMine query param: default to 'mine' tab
          if (this.$route.query.onlyMine == '1') {
            this.activeTab = 'mine'
          }
        }
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab
      if (tab === 'all') {
        this.allCurPage = 1
        this.allHasMore = true
      } else {
        this.mineCurPage = 1
        this.mineHasMore = true
      }
      if (tab === 'mine' || this.searchQuery.trim()) {
        this.fetchSessions(true)
      }
    },
    clearSearch() {
      this.searchQuery = ''
      this.allSessions = []
      this.mineSessions = []
      this.allHasLoaded = false
      this.mineHasLoaded = false
      this.allCurPage = 1
      this.mineCurPage = 1
      this.allHasMore = true
      this.mineHasMore = true
      if (this.activeTab === 'mine') {
        this.fetchSessions(true)
      }
    },
    search() {
      if (this.activeTab === 'all') {
        this.allCurPage = 1
        this.allHasMore = true
      } else {
        this.mineCurPage = 1
        this.mineHasMore = true
      }
      this.fetchSessions(true)
    },
    handleScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const viewportHeight = window.innerHeight
      const scrollHeight = document.documentElement.scrollHeight
      if (scrollTop + viewportHeight >= scrollHeight - 200) {
        this.loadMore()
      }
    },
    loadMore() {
      if (this.loading || !this.hasMore) return
      this.fetchSessions(false)
    },
    async onRefresh() {
      this.refreshing = true
      if (this.activeTab === 'all') {
        this.allCurPage = 1
        this.allHasMore = true
      } else {
        this.mineCurPage = 1
        this.mineHasMore = true
      }
      await this.fetchSessions(true)
      this.refreshing = false
    },
    async fetchSessions(reset) {
      if (this.activeTab !== 'mine' && !this.searchQuery.trim()) return

      const onlyMine = this.activeTab === 'mine'

      if (onlyMine) {
        if (this.mineLoading) return
        this.mineLoading = true
        if (reset) this.mineSessions = []
      } else {
        if (this.allLoading) return
        this.allLoading = true
        if (reset) this.allSessions = []
      }

      try {
        const curPage = onlyMine ? this.mineCurPage : this.allCurPage
        const response = await axios.post('/api/chat/search_sessions', {
          channelId: this.channelId,
          keyword: this.searchQuery.trim(),
          onlyMine: onlyMine,
          pageSize: this.pageSize,
          curPage: curPage
        })
        if (response.data.code === 0 && response.data.data) {
          const sessions = response.data.data.chatSessions || []
          if (onlyMine) {
            if (reset) {
              this.mineSessions = sessions
            } else {
              this.mineSessions = this.mineSessions.concat(sessions)
            }
            this.mineHasMore = sessions.length >= this.pageSize
            this.mineCurPage++
          } else {
            if (reset) {
              this.allSessions = sessions
            } else {
              this.allSessions = this.allSessions.concat(sessions)
            }
            this.allHasMore = sessions.length >= this.pageSize
            this.allCurPage++
          }
        } else {
          if (reset) {
            if (onlyMine) this.mineSessions = []
            else this.allSessions = []
          }
        }
      } catch (error) {
        console.error('[ChannelSearch] error:', error)
        Toast.fail(this.$t('dataLoadingFailed'))
      } finally {
        if (onlyMine) {
          this.mineLoading = false
          this.mineHasLoaded = true
        } else {
          this.allLoading = false
          this.allHasLoaded = true
        }
      }
    },
    openSessionDetail(session) {
      this.$router.push({
        path: '/channel/' + this.channelId,
        query: { chatSessionId: session.chatSessionId }
      })
    }
  }
}
</script>

<style scoped>
.channel-search-page {
  background: #F7F5F2;
  min-height: 100vh;
}

.channel-search-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border: 1px solid #E0DDD8;
}

.channel-search-search-inner {
  display: flex;
  align-items: center;
  background: #E0DDD8;
  border-radius: 20px;
  padding: 0 12px;
  height: 36px;
}

.search-popup-tabs {
  display: flex;
  background: white;
  border: 1px solid #E0DDD8;
}

.search-popup-tab {
  flex: 1;
  text-align: center;
  padding: 10px 0;
  font-size: 14px;
  color: #8A8A8A;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
}

.search-popup-tab.active {
  color: #E8543A;
  font-weight: 600;
}

.search-popup-tab.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 2px;
  background: #E8543A;
  border-radius: 1px;
}

.search-results {
  padding: 0 12px;
}

.search-result-item {
  padding: 12px;
  background: white;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  text-align: left;
}

.result-usermsg {
  font-size: 14px;
  color: #1A1A1A;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-ai-summary {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 11px;
  color: #8A8A8A;
  line-height: 1.4;
  margin-top: 4px;
}

.result-ai-summary.has-user-msg {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.result-ai-icon {
  font-size: 11px;
  flex-shrink: 0;
  margin-top: 1px;
}

.result-ai-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #8A8A8A;
  margin-top: 8px;
}

.result-avatar {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
}

.result-avatar-placeholder {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  font-weight: bold;
  flex-shrink: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/ChannelSearchSessions.vue